import React, { useEffect, useRef, useState } from 'react';
import { Button, MessageBar, MessageBarBody, Text } from '@fluentui/react-components';
import { ArrowLeftRegular } from '@fluentui/react-icons';
import { useNavigate } from 'react-router-dom';
import { DisclaimerEditorForm } from '../components/CustomDisclaimers/DisclaimerEditorForm';
import { useSavingGuard } from '../hooks/useSavingGuard';
import useEventTracker from '../hooks/useEventTracker';
import { customDisclaimerService } from '../services/customDisclaimerService';
import { CustomDisclaimerServiceError } from '../services/customDisclaimerServiceError';
import type { DisclaimerDraft, DisclaimerFieldErrors } from '../types/customDisclaimers';
import { ElementButton, PageName } from '../tracking/tracking.types';
import {
  createEmptyDisclaimerDraft,
  createDisclaimerCreatedNotice,
  hasDisclaimerFieldErrors,
  normalizeDisclaimerDraft,
  validateDisclaimerDraft,
} from '../utils/customDisclaimerUtils';
import { useStyles } from './DisclaimerFormPage.style';

export const CreateDisclaimer: React.FC = () => {
  const styles = useStyles();
  const navigate = useNavigate();
  const eventTracker = useEventTracker();
  const eventTrackerRef = useRef(eventTracker);
  const [draft, setDraft] = useState<DisclaimerDraft>(createEmptyDisclaimerDraft);
  const [errors, setErrors] = useState<DisclaimerFieldErrors>({});
  const [pageError, setPageError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const { requestNavigation } = useSavingGuard(saving);
  const isValid = !hasDisclaimerFieldErrors(validateDisclaimerDraft(draft));

  useEffect(() => {
    eventTrackerRef.current.eventTrackOnAppLaunched({ pageName: PageName.CREATE_DISCLAIMER }).catch(() => undefined);
  }, []);

  const trackElementClick = (elementName: ElementButton) => {
    eventTrackerRef.current.eventTrackOnElementClicked({
      elementType: 'button',
      elementName,
      pageName: PageName.CREATE_DISCLAIMER,
    }).catch(() => undefined);
  };

  const handleDraftChange = (nextDraft: DisclaimerDraft) => {
    const nextValidation = validateDisclaimerDraft(nextDraft);
    setDraft(nextDraft);
    setPageError(null);
    setErrors((currentErrors) => ({
      name: currentErrors.name ? nextValidation.name : undefined,
      organisationName: currentErrors.organisationName ? nextValidation.organisationName : undefined,
      text: currentErrors.text ? nextValidation.text : undefined,
    }));
  };

  const handleCancel = () => {
    trackElementClick(ElementButton.CANCEL_DISCLAIMER);
    requestNavigation(() => navigate('/custom-disclaimers'));
  };

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (saving) {
      return;
    }

    const validationErrors = validateDisclaimerDraft(draft);
    if (hasDisclaimerFieldErrors(validationErrors)) {
      setErrors(validationErrors);
      return;
    }

    setSaving(true);
    setErrors({});
    setPageError(null);

    try {
      const createdDisclaimer = await customDisclaimerService.create(normalizeDisclaimerDraft(draft));
      trackElementClick(ElementButton.SAVE_DISCLAIMER);

      navigate('/custom-disclaimers', {
        state: {
          disclaimerNotice: createDisclaimerCreatedNotice(
            createdDisclaimer.name,
            createdDisclaimer.policyIds.length,
          ),
        },
      });
    } catch (error) {
      const errorCode = error instanceof CustomDisclaimerServiceError
        ? error.code
        : (error as { code?: string })?.code;

      if (errorCode === 'duplicate-name') {
        setErrors({ name: 'A disclaimer with this name already exists.' });
      } else {
        setPageError('There was a problem creating the disclaimer. Try again later.');
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.container} data-testid="create-disclaimer-container-div">
      {pageError && (
        <MessageBar intent="error" className={styles.pageMessage} data-testid="create-disclaimer-error-message">
          <MessageBarBody>{pageError}</MessageBarBody>
        </MessageBar>
      )}

      <div className={styles.header} data-testid="create-disclaimer-header-div">
        <Button
          className={styles.backButton}
          appearance="subtle"
          icon={<ArrowLeftRegular />}
          onClick={handleCancel}
          disabled={saving}
          data-testid="create-disclaimer-back-button"
        >
          Disclaimers
        </Button>
        <Text className={styles.title} data-testid="create-disclaimer-title-text">
          New disclaimer
        </Text>
      </div>

      <div className={styles.content} data-testid="create-disclaimer-content-div">
        <div className={styles.inner}>
          <form className={styles.formCard} onSubmit={handleSubmit} noValidate>
            <Text className={styles.cardHeading}>Disclaimer details</Text>
            <Text className={styles.cardDescription}>
              Configure a disclaimer for users assigned to the policies you select. Changes can take up to 2 hours to take effect.
            </Text>
            <DisclaimerEditorForm
              draft={draft}
              onChange={handleDraftChange}
              errors={errors}
              disabled={saving}
            />
            <div className={styles.actions}>
              <Button
                appearance="secondary"
                type="button"
                onClick={handleCancel}
                disabled={saving}
                data-testid="create-disclaimer-cancel-button"
              >
                Cancel
              </Button>
              <Button
                appearance="primary"
                type="submit"
                disabled={!isValid || saving}
                data-testid="create-disclaimer-submit-button"
              >
                {saving ? 'Creating...' : 'Create disclaimer'}
              </Button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default CreateDisclaimer;
